import {DecimalPipe} from '@angular/common'
import {Component, computed, inject, OnInit, signal} from '@angular/core'
import {MatButton} from '@angular/material/button'
import {MatTab, MatTabContent, MatTabGroup} from '@angular/material/tabs'
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router'
import {DataStoreItem} from '@sparbanken-syd/datastore'
import {Applicant, KalpDataItem, Kalpylator} from '@sparbanken-syd/kalpylator'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {distinctUntilChanged, switchMap} from 'rxjs'
import {filter} from 'rxjs/operators'
import {KALP_ROUTE_PATH, START_ROUTE_PATH} from '../../application/data-types'
import {TestComponent} from '../../common/test/test.component'
import {BorgoService} from '../../services/borgo.service'
import {DatastoreService} from '../../services/datastore.service'
import {InterestService} from '../../services/interest.service'
import {KalpService} from '../../services/kalp.service'
import {KalpDisplayComponent} from '../0-kalp-display/kalp-display.component'
import {SliderComponent} from '../91-slider/slider.component'

@Component({
  selector: 'spb-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss'],
  imports: [
    TestComponent,
    MatTabGroup,
    MatTab,
    MatTabContent,
    KalpDisplayComponent,
    SliderComponent,
    RouterLink,
    MatButton,
    DecimalPipe
  ]
})
export class ResultComponent implements OnInit {
  public borgoService = inject(BorgoService)

  public borgo = signal(new Kalpylator())
  public sparbanken = signal(new Kalpylator())

  /**
   * The new loan as calculated
   */
  public newLoan = signal(0)

  /**
   * These are values set for debt quota warning.
   * If BORGO && dbq > 6 IF Age < 35, or > 5 if age >= 35
   */
  public debtQuota = signal<number | null>(0)

  public maxDebtQuota = 5 // If applicants older than 35

  public age = signal(0)

  public loanSliderMax = computed(() => Math.max(this.newLoan() * 5, 5000000))

  /**
   * Back destination to navigate in template
   */
  public backDestination: string[] = []

  /**
   * The base back destination that we spice up with applicants
   */
  private readonly backDestinationBase: string[] = ['/', KALP_ROUTE_PATH]

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  private readonly dateStoreService = inject(DatastoreService)
  private readonly interestService = inject(InterestService)
  private readonly kalpService = inject(KalpService)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)

  public ngOnInit(): void {
    //Need to reload the latest interest rate if the page reloads.
    this.interestService.getLatestInterest().subscribe()

    this.route.paramMap.pipe(
      filter((params: ParamMap) => !!params.get('id')),
      switchMap((params: ParamMap) => this.dateStoreService.getItem('KALP', params.get('id') as string))
    ).subscribe((item: DataStoreItem) => {
      const items = [
        {obs: this.kalpService.applicants$, key: 'applicants'},
        {obs: this.kalpService.incomes$, key: 'incomes'},
        {obs: this.kalpService.children$, key: 'children'},
        {obs: this.kalpService.cars$, key: 'cars'},
        {obs: this.kalpService.properties$, key: 'properties'},
        {obs: this.kalpService.loans$, key: 'loans'}
      ]

      items.forEach((d: any) => {
        d.obs.next(item.data.kalpItems.filter((i: KalpDataItem) => i.type === d.key))
      })
      this.borgoService.borgoPossible.set(item.data.borgoPossible)
    })

    this.kalpService.changes$.pipe(
      this.takeUntilDestroyed(),
      distinctUntilChanged()
    ).subscribe(() => {
      const newBorgo = this.kalpService.getKalpylator('borgo')
      const newSparbanken = this.kalpService.getKalpylator('sparbanken')

      newBorgo.calculate()
      newSparbanken.calculate()
      this.borgo.set(newBorgo)
      this.sparbanken.set(newSparbanken)
      this.newLoan.set(newSparbanken.newLoanTotalAmount)

      /**
       * These should be same for both and can be used for setting
       * the warning for LTI when Borgo and out of bounds
       */
      this.debtQuota.set(newSparbanken.debtQuota)

      const calculatedAge = Math.round(newSparbanken.applicantsAverageAge)
      this.age.set(calculatedAge)

      if (calculatedAge < 36) {
        this.maxDebtQuota = 6
      }
      // Set back to the applicants pnr
      const applicants: string[] = newSparbanken.applicants.map((a: Applicant) => a.sub as string)
      this.backDestination = this.backDestinationBase.concat(applicants)
    })
  }

  public goToIncome(): void {
    this.kalpService.lastStep = 1
  }

  public reset(): void {
    this.kalpService.reset()
    this.router.navigate([START_ROUTE_PATH]).then()
  }

  public calculate2p(percentage: number): void {
    // Create copies or mutate? Since Kalpylator is complex, we mutate and signal update.
    const sp = this.sparbanken()
    const bo = this.borgo()

    sp.setIncreasedInterest(percentage * 100)
    bo.setIncreasedInterest(percentage * 100)

    this.sparbanken.set(sp)
    this.borgo.set(bo)
  }

  public setIncome(income: number): void {
    const sp = this.sparbanken()
    const bo = this.borgo()

    sp.monthlyIncomeOverride = income
    bo.monthlyIncomeOverride = income

    this.sparbanken.set(sp)
    this.borgo.set(bo)
  }

  public setTotalLoan(loan: number): void {
    const sp = this.sparbanken()
    const bo = this.borgo()

    sp.newLoanTotalAmountOverride = loan
    bo.newLoanTotalAmountOverride = loan

    this.sparbanken.set(sp)
    this.borgo.set(bo)

    this.debtQuota.set(sp.debtQuota)
  }
}
